import React from "react";
import UserFallback from "../../assets/images/user.jpg";

/**
 * A drop-in replacement for `<img>` that never shows the browser's broken-image
 * icon.
 *
 * Remote media here is S3-backed and a deleted object answers **403**, so a
 * broken `src` is a normal outcome for older rows — not an edge case. Every
 * `<img>` in this app passed a fallback for a MISSING url (`src={x || Dummy}`)
 * but none handled a url that exists and then fails to load, which is the case
 * that actually renders the broken-icon glyph.
 *
 * Pass `fallback` for non-avatar media (e.g. a poster); it defaults to the user
 * placeholder, matching the `DummyIcon` the avatar call sites already used.
 * All other props are forwarded, so this swaps in without touching layout.
 */
const SafeImage = ({ src, fallback = UserFallback, alt = "", ...rest }) => {
  const [failed, setFailed] = React.useState(false);

  // Re-arm when the src changes, so a previously-failed row doesn't keep
  // showing the fallback after the list re-renders with a different image.
  React.useEffect(() => {
    setFailed(false);
  }, [src]);

  const resolved = !src || failed ? fallback : src;

  return (
    <img
      {...rest}
      alt={alt}
      src={resolved}
      onError={() => {
        // Guard against a loop if the fallback itself ever fails to load.
        if (!failed) setFailed(true);
      }}
    />
  );
};

export default SafeImage;
